import { cn } from "@istari-frontend/shared";
import { Avatar, AvatarFallback, Button } from "@istari-frontend/ui";
import { X } from "lucide-react";
import React from "react";

type User = {
  id?: string;
  userId?: string;
  subjectId?: string;
  firstName?: string | null;
  lastName?: string | null;
  displayName?: string | null;
  label?: string;
  email?: string | null;
  subjectInfo?: {
    email?: string;
    username?: string;
  };
};

type SharedUserListProps = {
  children: React.ReactNode;
  className?: string;
};

export function SharedUserList({ children, className }: SharedUserListProps) {
  return (
    <div className={cn("rounded-md border", className)} role="list">
      {children}
    </div>
  );
}

type SharedUserListItemProps = {
  user: User;
  isCurrentUser?: boolean;
  onRemove: () => void;
  actionElement: React.ReactNode;
  showRemoveButton?: boolean;
  isInvalidEmail?: boolean;
  rightElement?: React.ReactNode;
};

export function SharedUserListItem({
  user,
  isCurrentUser = false,
  onRemove,
  actionElement,
  showRemoveButton = true,
  isInvalidEmail = false,
  rightElement,
}: SharedUserListItemProps) {
  const userId = user.userId || user.subjectId || user.id;

  const getUserInitials = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName[0]}${user.lastName[0]}`.toUpperCase();
    }
    if (user.displayName) {
      const parts = user.displayName.split(" ");
      return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0][0].toUpperCase();
    }
    if (user.subjectInfo?.username) {
      const parts = user.subjectInfo.username.split(" ");
      return parts.length > 1 ? `${parts[0][0]}${parts[1][0]}`.toUpperCase() : parts[0][0].toUpperCase();
    }
    if (user.label) {
      return user.label[0].toUpperCase();
    }
    if (user.subjectInfo?.email) {
      return user.subjectInfo.email[0].toUpperCase();
    }
    return "?";
  };

  const getUserDisplayName = () => {
    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}${isCurrentUser ? " (You)" : ""}`;
    }
    if (user.displayName) {
      return `${user.displayName}${isCurrentUser ? " (You)" : ""}`;
    }
    if (user.subjectInfo?.username) {
      return user.subjectInfo.username;
    }
    if (user.label) {
      return user.label;
    }
    if (user.subjectInfo?.email) {
      return user.subjectInfo.email;
    }
    return "Unknown User";
  };

  return (
    <div
      data-testid={userId ? `user-list-item-${userId}` : "user-list-item"}
      className="grid grid-cols-[1fr_auto_auto] items-center gap-2 border-b-[0.5px] px-2.5 py-2 first:rounded-t-md last:rounded-b-md last:border-b-0"
      role="listitem"
    >
      <div className="flex min-w-0 items-center gap-2">
        <Avatar className="size-7">
          <AvatarFallback className="size-7 text-xs">{getUserInitials()}</AvatarFallback>
        </Avatar>
        <div className="flex min-w-0 flex-col">
          <span
            className={cn("text-foreground truncate text-sm", isInvalidEmail && "text-red-500")}
            title={getUserDisplayName()}
          >
            {getUserDisplayName()}
            {isInvalidEmail && <span className="ml-1">(Invalid email)</span>}
          </span>
          {(user.email || user.subjectInfo?.email) && (
            <span className="text-muted-foreground truncate text-xs leading-tight font-light">
              {user.email || user.subjectInfo?.email}
            </span>
          )}
        </div>
      </div>

      <div className="flex items-center justify-end">{actionElement}</div>

      <div className="flex items-center justify-end">
        {rightElement ? (
          rightElement
        ) : showRemoveButton ? (
          <Button
            data-testid="share-popup-remove-user-access-button"
            variant="ghost"
            size="icon"
            className="[&_svg]:text-muted-foreground hover:[&_svg]:text-foreground focus:[&_svg]:text-foreground h-6 w-6 [&_svg]:opacity-50 hover:[&_svg]:opacity-100 focus:[&_svg]:opacity-100"
            onClick={onRemove}
          >
            <X className="size-4" />
          </Button>
        ) : (
          <div className="h-6 w-6" />
        )}
      </div>
    </div>
  );
}
